import { useEffect, useRef, useState } from "react";

interface UseScrollAnimationOptions {
  threshold?: number;
  rootMargin?: string;
  once?: boolean;
}

export const useScrollAnimation = (options: UseScrollAnimationOptions = {}) => {
  const { threshold = 0.1, rootMargin = "0px 0px -60px 0px", once = true } = options;
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (once) observer.unobserve(el);
        } else if (!once) {
          setIsVisible(false);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold, rootMargin, once]);

  return { ref, isVisible };
};

// Reusable animated wrapper
import React from "react";

interface AnimatedSectionProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  animation?: "fade-up" | "fade-right" | "scale" | "fade";
}

export const AnimatedSection = ({ children, className = "", delay = 0, animation = "fade-up" }: AnimatedSectionProps) => {
  const { ref, isVisible } = useScrollAnimation();

  const animationStyles: Record<string, { initial: React.CSSProperties; visible: React.CSSProperties }> = {
    "fade-up": {
      initial: { opacity: 0, transform: "translateY(30px)" },
      visible: { opacity: 1, transform: "translateY(0)" },
    },
    "fade-right": {
      initial: { opacity: 0, transform: "translateX(30px)" },
      visible: { opacity: 1, transform: "translateX(0)" },
    },
    scale: {
      initial: { opacity: 0, transform: "scale(0.95)" },
      visible: { opacity: 1, transform: "scale(1)" },
    },
    fade: {
      initial: { opacity: 0 },
      visible: { opacity: 1 },
    },
  };

  const style = animationStyles[animation] || animationStyles["fade-up"];

  return (
    <div
      ref={ref}
      className={className}
      style={{
        ...(isVisible ? style.visible : style.initial),
        transition: `all 0.6s cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
        willChange: "opacity, transform",
      }}
    >
      {children}
    </div>
  );
};
